const API_URL = '';

class ApiService {
  constructor() {
    this.baseURL = API_URL;
    this.token = localStorage.getItem('authToken');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('authToken', token);
    } else {
      localStorage.removeItem('authToken');
    }
  }

  getToken() {
    return this.token || localStorage.getItem('authToken');
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const maxRetries = 4;
    const timeoutMs = options.timeout || 45000; // 45 second timeout for slow connections

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // Add AbortController timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        const response = await fetch(url, {
          ...options,
          headers,
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (response.status >= 502 && response.status <= 520 && attempt < maxRetries) {
          await new Promise(r => setTimeout(r, 3000 * (attempt + 1)));
          continue;
        }

        // Server error (likely DB cold start) - retry
        if (response.status >= 500 && attempt < maxRetries) {
          await new Promise(r => setTimeout(r, 3000 * (attempt + 1)));
          continue;
        }

        if (!response.ok) {
          let errorDetail = 'Request failed';
          try {
            const cloned = response.clone();
            const text = await cloned.text();
            const data = JSON.parse(text);
            errorDetail = data.detail || errorDetail;
            if (response.status === 401 && token && !endpoint.includes('/auth/login') && /token|credentials/i.test(errorDetail)) {
              this.handleSessionExpired();
            }
          } catch(e) {
            // If body can't be read, map common status codes
            if (response.status === 401) errorDetail = 'Invalid credentials';
            else if (response.status === 403) errorDetail = 'Access denied';
            else if (response.status === 404) errorDetail = 'Not found';
            else if (response.status === 422) errorDetail = 'Validation error';
          }
          throw new Error(errorDetail);
        }

        return await response.json();
      } catch (error) {
        const isTimeout = error.name === 'AbortError';
        const isNetwork = error.message === 'Failed to fetch' || error.message === 'NetworkError' || isTimeout;

        if (isNetwork && attempt < maxRetries) {
          console.log(`Request to ${endpoint} failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying...`);
          await new Promise(r => setTimeout(r, 3000 * (attempt + 1)));
          continue;
        }

        if (isTimeout) {
          throw new Error('Request timed out. Please try again.');
        }

        console.error('API Request Error:', error);
        throw error;
      }
    }
  }

  // Authentication APIs
  async login(email, password) {
    const response = await this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    
    if (response.access_token) {
      this.setToken(response.access_token);
    }
    
    return response;
  }

  async register(userData) {
    return await this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async getUsers() {
    return await this.request('/api/auth/users');
  }

  logout() {
    this.setToken(null);
    localStorage.removeItem('user');
  }

  handleSessionExpired() {
    if (this._expiredHandled) return;
    this._expiredHandled = true;
    const isParent = window.location.pathname.startsWith('/parent');
    const isStudent = window.location.pathname.startsWith('/student');
    this.logout();
    const target = isParent ? '/parent/login' : isStudent ? '/student/login' : '/login';
    window.location.assign(`${target}?expired=1`);
  }

  // Grade APIs
  async createGrade(gradeData) {
    return await this.request('/api/grades/', {
      method: 'POST',
      body: JSON.stringify(gradeData),
    });
  }

  async getStudentGrades(studentId) {
    return await this.request(`/api/grades/student/${studentId}`);
  }

  async getAssessmentGrades(assessmentId) {
    return await this.request(`/api/grades/assessment/${assessmentId}`);
  }

  async bulkCreateGrades(gradesArray) {
    return await this.request('/api/grades/bulk', {
      method: 'POST',
      body: JSON.stringify(gradesArray),
    });
  }

  async quickGradeEntry(classId, assessmentType, term, maxMarks, grades, strand = null, customName = null) {
    return await this.request('/api/grades/quick-entry', {
      method: 'POST',
      body: JSON.stringify({
        class_id: classId,
        assessment_type: assessmentType,
        assessment_name: customName || assessmentType, // Use custom name or fall back to type
        strand: strand,
        term: term,
        max_marks: maxMarks,
        grades: grades
      }),
    });
  }

  async deleteGrade(gradeId) {
    return await this.request(`/api/grades/${gradeId}`, {
      method: 'DELETE',
    });
  }

  async getClassGrades(classId) {
    return await this.request(`/api/grades/class/${classId}`);
  }

  async getStudentReportCard(studentId, year, term = "Term 1") {
    return await this.request(`/api/report-cards/${studentId}?year=${year}&term=${term}`);
  }

  // Parent Portal APIs
  async parentLogin(phoneNumber, pin) {
    return await this.request('/api/parent/login', {
      method: 'POST',
      body: JSON.stringify({
        phone_number: phoneNumber,
        pin: pin
      })
    });
  }

  async studentLogin(studentEmail, password) {
    return await this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ 
        email: studentEmail, 
        password 
      })
    });
  }

  async getParentReportCards(token) {
    return await this.request(`/api/parent/report-cards?token=${token}`);
  }

  async getParentReportCard(token, studentId, year, term) {
    return await this.request(`/api/parent/report-card/${studentId}?token=${token}&year=${year}&term=${term}`);
  }

  async changeParentPIN(token, oldPin, newPin) {
    return await this.request(`/api/parent/change-pin?token=${token}`, {
      method: 'POST',
      body: JSON.stringify({
        old_pin: oldPin,
        new_pin: newPin
      })
    });
  }

  // Admin APIs
  async publishReportCards(year, term) {
    return await this.request('/api/admin/publish-reports', {
      method: 'POST',
      body: JSON.stringify({ year, term })
    });
  }

  async getPublishedReports() {
    return await this.request('/api/admin/published-reports');
  }

  async createParentAccountsBulk(formData) {
    const token = this.getToken();
    
    return await fetch(`${this.baseURL}/api/admin/create-parent-accounts-bulk`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    }).then(async (response) => {
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || `HTTP ${response.status}`);
      }
      return response.json();
    });
  }

  // Assessment APIs
  async createAssessment(assessmentData) {
    return await this.request('/api/assessments/', {
      method: 'POST',
      body: JSON.stringify(assessmentData),
    });
  }

  async listAssessments(classId = null) {
    const query = classId ? `?class_id=${classId}` : '';
    return await this.request(`/api/assessments/${query}`);
  }

  async getAssessment(assessmentId) {
    return await this.request(`/api/assessments/${assessmentId}`);
  }

  async updateAssessment(assessmentId, assessmentData) {
    return await this.request(`/api/assessments/${assessmentId}`, {
      method: 'PUT',
      body: JSON.stringify(assessmentData),
    });
  }

  async deleteAssessment(assessmentId) {
    return await this.request(`/api/assessments/${assessmentId}`, {
      method: 'DELETE',
    });
  }

  // Submission APIs
  async createSubmission(assessmentId) {
    return await this.request('/api/submissions/', {
      method: 'POST',
      body: JSON.stringify({ assessment_id: assessmentId }),
    });
  }

  async listSubmissions(assessmentId = null) {
    const query = assessmentId ? `?assessment_id=${assessmentId}` : '';
    return await this.request(`/api/submissions/${query}`);
  }

  async getSubmission(submissionId) {
    return await this.request(`/api/submissions/${submissionId}`);
  }

  async submitSubmission(submissionId) {
    return await this.request(`/api/submissions/${submissionId}/submit`, {
      method: 'PUT',
    });
  }

  async gradeSubmission(submissionId, scoreTotal) {
    return await this.request(`/api/submissions/${submissionId}/grade`, {
      method: 'PUT',
      body: JSON.stringify({ score_total: scoreTotal }),
    });
  }

  // Student APIs
  async listStudents(year = null) {
    const query = year ? `?year=${year}` : '';
    return await this.request(`/api/students/${query}`);
  }

  async getStudent(studentId) {
    return await this.request(`/api/students/${studentId}`);
  }

  async createStudent(data) {
    return await this.request(`/api/students/`, { method: 'POST', body: JSON.stringify(data) });
  }

  async removeStudent(studentId) {
    return await this.request(`/api/students/${studentId}`, { method: 'DELETE' });
  }

  async updateStudent(studentId, data) {
    return await this.request(`/api/students/${studentId}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async listRemovedStudents() {
    return await this.request(`/api/students/removed`);
  }

  async restoreStudent(studentId) {
    return await this.request(`/api/students/${studentId}/restore`, { method: 'POST' });
  }

  async importStudents(file, defaultYear = null) {
    const form = new FormData();
    form.append('file', file);
    if (defaultYear) form.append('default_year', defaultYear);
    const res = await fetch(`${this.baseURL}/api/students/import`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${this.getToken()}` },
      body: form,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || `Import failed (${res.status})`);
    return data;
  }

  // Classes APIs
  async listClasses(teacherId = null, year = null) {
    const params = new URLSearchParams();
    if (teacherId) params.append('teacher_id', teacherId);
    if (year) params.append('year', year);
    const query = params.toString() ? `?${params.toString()}` : '';
    return await this.request(`/api/classes/${query}`);
  }

  async getTeacherSubjects(teacherId = null) {
    const query = teacherId ? `?teacher_id=${teacherId}` : '';
    return await this.request(`/api/classes/subjects${query}`);
  }

  // Teacher Classes APIs
  async getTeacherClasses(skipCounts = false, year = null, subject = null) {
    const params = new URLSearchParams();
    if (skipCounts) params.append('skip_counts', 'true');
    if (year) params.append('year', year);
    if (subject) params.append('subject', subject);
    const query = params.toString() ? `?${params.toString()}` : '';
    return await this.request(`/api/classes/${query}`);
  }

  async getClassStudents(classId) {
    return await this.request(`/api/classes/${classId}/students`);
  }

  // Analytics APIs
  async getPerformanceSummary(year = null, subject = null) {
    const params = new URLSearchParams();
    if (year) params.append('year', year);
    if (subject) params.append('subject', subject);
    const query = params.toString() ? `?${params.toString()}` : '';
    return await this.request(`/api/analytics/performance-summary${query}`);
  }

  async getStudentPerformance(studentId) {
    return await this.request(`/api/analytics/student-performance/${studentId}`);
  }

  // Password change
  async changePassword(passwordData) {
    return await this.request('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(passwordData)
    });
  }

  // Health check
  async healthCheck() {
    return await this.request('/api/health');
  }

  // Principal Comments API
  async savePrincipalComment(commentData) {
    return await this.request('/api/admin/principal-comment', {
      method: 'POST',
      body: JSON.stringify(commentData)
    });
  }

  // Curriculum APIs
  async getCurriculumData(classId) {
    return await this.request(`/api/curriculum/${classId}`);
  }

  async getTermCurriculum(classId, term) {
    return await this.request(`/api/curriculum/${classId}/term/${term}`);
  }

  // Schools APIs
  async getAllSchools() {
    return await this.request('/api/schools');
  }

  async getSchoolDetails(schoolId) {
    return await this.request(`/api/schools/${schoolId}`);
  }

  async getSchoolsByType(schoolType) {
    return await this.request(`/api/schools/type/${schoolType}`);
  }

  async getSchoolClasses(schoolId) {
    return await this.request(`/api/schools/${schoolId}/classes`);
  }

  // Student Subjects APIs (Years 11-13 stream-based subjects)
  async getStreamDefinitions() {
    return await this.request('/api/student-subjects/streams');
  }

  async getStudentSubjects(studentId, year = null) {
    const query = year ? `?year=${year}` : '';
    return await this.request(`/api/student-subjects/student/${studentId}${query}`);
  }

  async getStudentReportCardSubjects(studentId, year) {
    return await this.request(`/api/student-subjects/report-card/${studentId}?year=${year}`);
  }

  async assignStudentSubjects(studentId, subjects, stream, year) {
    return await this.request('/api/student-subjects/assign', {
      method: 'POST',
      body: JSON.stringify({
        student_id: studentId,
        subjects,
        stream,
        year
      })
    });
  }

  async toggleStudentSubject(studentId, subject, year, active = true) {
    return await this.request(`/api/student-subjects/student/${studentId}/toggle-subject?subject=${encodeURIComponent(subject)}&year=${year}&active=${active}`, {
      method: 'PUT'
    });
  }

  async bulkAssignStream(year, stream, studentIds, optionalSubject) {
    return await this.request(`/api/student-subjects/bulk-assign?year=${year}&stream=${stream}&optional_subject=${encodeURIComponent(optionalSubject)}`, {
      method: 'POST',
      body: JSON.stringify(studentIds)
    });
  }
  // Student Attendance (Roll Call) APIs
  async saveStudentAttendance(attendanceData) {
    return await this.request('/api/student-attendance/save', {
      method: 'POST',
      body: JSON.stringify(attendanceData)
    });
  }

  async getStudentAttendance(studentId, term = null, year = null) {
    const params = new URLSearchParams();
    if (term) params.append('term', term);
    if (year) params.append('year', year);
    const query = params.toString() ? `?${params.toString()}` : '';
    return await this.request(`/api/student-attendance/student/${studentId}${query}`);
  }

  async getClassAttendance(classId, date = null) {
    const query = date ? `?date_str=${date}` : '';
    return await this.request(`/api/student-attendance/class/${classId}${query}`);
  }

  async getWeeklyAttendanceSummary() {
    return await this.request('/api/student-attendance/weekly-summary');
  }

  async getClassWeeklyAttendance(classId) {
    return await this.request(`/api/student-attendance/class-weekly/${classId}`);
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;
